import PortableText, {
  PortablePTag,
} from '@otovo/shared/components/Sanity/PortableTextComponents';
import SanityImage from '@otovo/shared/components/Sanity/SanityImage';
import { useGlobalConfig } from '@otovo/shared/hooks/configContext';
import { Otovo$Locale } from '@otovo/shared/types/otovoweb';
import { Sanity$FeatureColumnComparison } from '@otovo/shared/types/sanityTypes';
import cx from 'classnames';
import { stegaClean } from 'next-sanity';
import { useRouter } from 'next/router';
import CTAButton from '../../v2/CTAButton';
import CTALink from '../../v2/CTALink';
import { routeToHref } from '../routeToHrefUtil';

type Props = {
  content: Sanity$FeatureColumnComparison;
};

const ratioClassMapping: Record<
  Sanity$FeatureColumnComparison['imageRatio'],
  { aspectClasses: string; gridClasses: string }
> = {
  '1:1': {
    aspectClasses: 'aspect-square',
    gridClasses: 'md:grid-cols-4',
  },
  '3:4': {
    aspectClasses: 'w-full aspect-square md:aspect-[3/4]',
    gridClasses: 'md:grid-cols-3',
  },
};

const defaultRatioMapping = ratioClassMapping['3:4']; // Fallback for old pages without imageRatio

const FeatureColumnComparison = ({ content }: Props) => {
  const config = useGlobalConfig();
  const { locale } = useRouter() as { locale: Otovo$Locale };
  const safeImageRatioKey = stegaClean(content.imageRatio);

  const { aspectClasses, gridClasses } =
    ratioClassMapping[safeImageRatioKey] ?? defaultRatioMapping;

  return (
    <div
      className="color-scheme-white bg-surface"
      data-testid="feature-column-comparison"
    >
      <div className="mx-auto max-w-8xl px-4 py-16 md:px-6 md:py-20 lg:px-16 lg:py-24">
        <div className="grid-cols-12 md:grid">
          <div className="col-span-6 mb-8 space-y-4 md:mb-0">
            <h2 className="text-2xl font-semibold text-balance text-heading md:text-3xl lg:text-4xl">
              {content.heading}
            </h2>
            <PortablePTag
              className="text-base text-subheading md:text-xl"
              value={content.description}
            />

            {content.cta && (
              <CTAButton
                href={routeToHref(
                  content.cta.route,
                  config.BU_CONFIG.market,
                  locale,
                )}
                className="mt-2"
              >
                {content.cta.title}
              </CTAButton>
            )}
          </div>

          <div className="col-span-12 mt-20 md:mt-10">
            <div
              className={cx('gap-6 space-y-16 gap-y-16 md:grid', gridClasses)}
            >
              {content.columns.map((feature) => (
                <article
                  aria-labelledby={`feature-${feature._key}`}
                  key={feature._key}
                  className="relative md:mb-0"
                >
                  <div
                    className={cx(
                      'overflow-hidden rounded-lg bg-grey-10',
                      aspectClasses,
                    )}
                  >
                    <SanityImage
                      image={feature.image}
                      className="h-full w-full object-cover"
                      maxHeightFull
                      sizes="(max-width: 767px) 100vw, 350px"
                    />
                  </div>
                  <span className="mt-6 block text-base font-semibold text-subheading">
                    {feature.tagline}
                  </span>
                  <h3
                    id={`feature-${feature._key}`}
                    className="mt-3 block text-2xl font-semibold text-balance text-heading"
                  >
                    {feature.heading}
                  </h3>
                  <div className="mt-3 block text-body">
                    <PortableText value={feature.description} />
                  </div>
                  {feature.cta && (
                    <div className="mt-3">
                      <CTALink
                        href={routeToHref(
                          feature.cta.route,
                          config.BU_CONFIG.market,
                          locale,
                        )}
                      >
                        {feature.cta.title}
                      </CTALink>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureColumnComparison;
